import { useNavigate, useParams } from "react-router-dom";
import { v4 as uuid } from "uuid";
import { PageWrapper, PageWrapperProps } from "../../common-ui/PageWrapper";
import {
  InstrumentErrors,
  useInstrument,
  useUpdateInstrument
} from "./instrumentUtils";
import { Alert, CircularProgress, Typography } from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { SubmitHandler, useFieldArray, useForm } from "react-hook-form";
import {
  Assessment,
  CreateAnswerOptionInput,
  CreateQuestionInput,
  InputType,
  Instrument,
  Question,
  UpdateInstrumentInput
} from "../../API";
import { EditQuestion } from "../Questions/EditQuestion";
import {
  useCreateQuestion,
  useDeleteQuestion
} from "../Questions/questionUtils";
import {
  useCreateAnswerOption,
  useDeleteAnswerOption
} from "../AnswerOptions/answerOptionUtils";
import { useAssessmentsByInstrumentId } from "../Assessments/assessmentUtils";
import { useAppSelector } from "../../app/hooks";
import { selectUserCognitoGroups } from "../Users/usersSlice";
import { CognitoUserGroups } from "../Users/userUtils";

const pageDetails: PageWrapperProps = {
  className: "EditInstrument",
  headerText: "Edit Instrument"
};

export type InstrumentWithoutAuthor = Omit<Instrument, "author">;

export const EditInstrument = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currentUsersPermissions = useAppSelector(selectUserCognitoGroups);
  const isSysAdmin = currentUsersPermissions?.includes(
    CognitoUserGroups.SysAdmins
  );
  const {
    instrument,
    status: getInstrumentStatus,
    error: getInstrumentError,
    getInstrument
  } = useInstrument(id);
  const { deleteQuestion, status: deleteQuestionStatus } = useDeleteQuestion();
  const { deleteAnswerOption, status: deleteAnswerOptionStatus } =
    useDeleteAnswerOption();
  const { createQuestion, status: createQuestionStatus } = useCreateQuestion();
  const { createAnswerOption, status: createAnswerOptionStatus } =
    useCreateAnswerOption();
  const { updateInstrument, status: updateInstrumentStatus } =
    useUpdateInstrument();
  const {
    assessments,
    getAssessmentsByInstrumentId,
    status: assessmentsByInstrumentIdStatus
  } = useAssessmentsByInstrumentId(id);
  const {
    register,
    reset,
    control,
    handleSubmit,
    getValues,
    formState,
    watch
  } = useForm<InstrumentWithoutAuthor>();
  const {
    fields: questionFields,
    append: appendQuestion,
    remove: removeQuestion
  } = useFieldArray({
    name: "questions.items",
    control
  });

  const isAssessmentPublished = assessments?.[0].published ?? true;

  const { errors, isDirty } = formState;

  const anyApiIsLoading = [
    getInstrumentStatus,
    deleteQuestionStatus,
    deleteAnswerOptionStatus,
    createQuestionStatus,
    createAnswerOptionStatus,
    updateInstrumentStatus,
    assessmentsByInstrumentIdStatus
  ].includes("loading");

  const onSubmit: SubmitHandler<Instrument> = async (data) => {
    // Published assessments and their instruments cannot be updated
    if (isAssessmentPublished) return;
    // Jump out if user isn't a SysAdmin
    if (!isSysAdmin) return;
    // First, delete all existing questions and answers
    // Jump out if there isn't an existing instrument
    if (!instrument) return;
    const existingQuestions = instrument.questions?.items;
    if (existingQuestions) {
      for (const existingQuestion of existingQuestions) {
        const existingAnswers = existingQuestion?.answerOptions?.items;
        if (existingAnswers) {
          for (const existingAnswer of existingAnswers) {
            if (existingAnswer) {
              // Delete the existingAnswer
              const existingAnswerDeletionResult = await deleteAnswerOption(
                existingAnswer.id
              );
            }
          }
        }
        // Delete the existingQuestion
        if (existingQuestion) {
          const existingQuestionDeletionResult = await deleteQuestion(
            existingQuestion.id
          );
        }
      }
    }

    const newQuestions = data.questions?.items;
    if (newQuestions) {
      for (const newQuestion of newQuestions) {
        if (newQuestion) {
          // Need to create the question before creating the answerOptions for it
          const input: CreateQuestionInput = {
            instrumentId: instrument.id,
            defaultPrompt: newQuestion.defaultPrompt,
            helpText: newQuestion.helpText,
            inputType: newQuestion.inputType,
            selfAssessmentPrompt: newQuestion.selfAssessmentPrompt,
            sortingIndex: newQuestion.sortingIndex,
            title: newQuestion.title
          };
          const createNewQuestionResult = await createQuestion(input);
          if (createNewQuestionResult) {
            const newAnswers = newQuestion.answerOptions?.items;
            // There may be stale/unwanted answerOptions if the type went from radio to feedback.
            // Only add newAnswers if the type is radio.
            if (newQuestion.inputType === InputType.Radio && newAnswers) {
              for (const newAnswer of newAnswers) {
                if (newAnswer) {
                  // Create the newAnswer
                  const input: CreateAnswerOptionInput = {
                    questionId: createNewQuestionResult.id,
                    anchor: newAnswer.anchor,
                    helpText: newAnswer.helpText,
                    sortingIndex: newAnswer.sortingIndex,
                    value: newAnswer.value
                  };
                  const createNewAnswerOptionResult = await createAnswerOption(
                    input
                  );
                }
              }
            }
          }
        }
      }
    }

    // Update the base Instrument data
    const input: UpdateInstrumentInput = {
      description: data.description,
      title: data.title,
      id: instrument.id
    };
    const updateInstrumentResult = await updateInstrument(input);
    // Navigate back to the parent assessment
    if (updateInstrumentResult) {
      const assessmentsWithInstrumentId = await getAssessmentsByInstrumentId(
        updateInstrumentResult.id
      );
      const parentAssessment = assessmentsWithInstrumentId?.[0];
      if (parentAssessment) {
        navigate(`/assessments/${parentAssessment.id}`);
      }
    }
  };

  useEffect(() => {
    if (instrument) {
      reset({ ...instrument });
    }
  }, [instrument]);

  // Show an appropriate screen if the logged in user isn't a SysAdmin
  if (!isSysAdmin) {
    return (
      <PageWrapper {...pageDetails}>
        <Typography variant="h2">
          {t("You must be logged in as a SysAdmin to edit an Instrument.")}
        </Typography>
      </PageWrapper>
    );
  }

  if (getInstrumentStatus === "loading") {
    return (
      <PageWrapper {...pageDetails}>
        <CircularProgress />
      </PageWrapper>
    );
  }

  if (getInstrumentStatus === "succeeded" && instrument) {
    return (
      <PageWrapper {...pageDetails}>
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <Grid container spacing={2} flexDirection="column">
            <Grid xs={8}>
              <label htmlFor="title">{t("Instrument Title")}</label>
              <input
                type="text"
                required
                placeholder={"Instrument Name"}
                style={{ width: "100%" }}
                id="title"
                disabled={anyApiIsLoading || isAssessmentPublished}
                {...register("title", {
                  required: {
                    value: true,
                    message: InstrumentErrors.NoTitle
                  }
                })}
              />
              {errors.title?.message && (
                <Alert severity="error">{t(errors.title.message)}</Alert>
              )}
            </Grid>
            <Grid xs={8}>
              <label htmlFor="description">{t("Instrument Description")}</label>
              <input
                type="text"
                placeholder={"Instrument Description"}
                style={{ width: "100%" }}
                id="description"
                disabled={anyApiIsLoading || isAssessmentPublished}
                {...register("description")}
              />
            </Grid>
            <Grid container spacing={2} flexDirection={"column"}>
              Questions:
              <ul>
                {questionFields.map((question, index) => (
                  <EditQuestion
                    question={question}
                    anyApiIsLoading={anyApiIsLoading}
                    isAssessmentPublished={isAssessmentPublished}
                    register={register}
                    index={index}
                    removeQuestion={removeQuestion}
                    control={control}
                    questionIndex={index}
                    getValues={getValues}
                    formState={formState}
                    watch={watch}
                  />
                ))}
                <Grid xs={8}>
                  <button
                    type="button"
                    disabled={anyApiIsLoading || isAssessmentPublished}
                    onClick={() => {
                      const appendedQuestion: Question = {
                        // Dev Note: The unnecessary data will be stripped at time of submission.
                        __typename: "Question",
                        id: uuid(),
                        instrumentId: id || "",
                        defaultPrompt: "",
                        selfAssessmentPrompt: "",
                        title: "",
                        helpText: "",
                        inputType: InputType.Radio,
                        answerOptions: null,
                        sortingIndex: null,
                        createdAt: new Date().toISOString(),
                        updatedAt: new Date().toISOString()
                      };
                      appendQuestion(appendedQuestion);
                    }}
                  >
                    {t("Add Question")}
                  </button>
                </Grid>
              </ul>
            </Grid>
            <Grid xs={8}>
              <button disabled={anyApiIsLoading || isAssessmentPublished}>
                {t("Save")}
              </button>
            </Grid>
          </Grid>
        </form>
      </PageWrapper>
    );
  }

  return <div>No criteria met.</div>;
};
